import React, { useCallback, useEffect, useRef, useState } from 'react';
import HomeAddressFields from './components/HomeAddressFields/HomeAddressFields';
import EmailInput from './components/EmailInput/EmailInput';
import AutoFillFocusProvider from '../../../providers/AutoFillFocusProvider';
import StateDropdownInput from './components/StateDropdown/StateDropdown';
import MailingAddressInput from './components/MailingAddressInput/MailingAddressInput';
import { TPersonalInformation, usePersonalInformation } from '../../../providers/PersonalInformationProvider';
import { usePersonalInformationError } from '../../../providers/PersonalInformationErrorProvider';
import useLoqate from '../../../../../hooks/useLoqate';
import { IErrorState } from '../../../providers/reducers/ErrorReducer';
import { useHomeAddress } from '../../../providers/HomeAddressProvider';
import { useHomeAddressError } from '../../../providers/HomeAddressErrorProvider';
import useGeneralDelivery from '../../../../../hooks/useGeneralDelivery';
import { regexValidation } from '../../../../../utils/cob-utils';
import { usePageProperties } from '../../../providers/PagePropertiesProvider';
import { homeAddressCheckRegex, stateMap } from '../../../../../utils/acquisition-utils';
import NameField from '../../../../FieldContainers/NameField/NameField';
import { namesAndIds } from '../../../../../types/shared/PrequalificationDataEntry';
import { MiddleInitialField } from '../../../../FieldContainers/MiddleInitialField/MiddleInitialField';
import DateOfBirthField from '../../../../FieldContainers/DateOfBirthField/DateOfBirthField';
import { useAddressValidationState } from '../../../../../components/PqDataEntryForm/providers/AddressValidationProvider';
import { addressValidationSemaphore } from '../../../../../components/PqDataEntryForm/util/addressValidationSemaphore';
import SsnField from '../../../../FieldContainers/SsnField/SsnField';
import ApartmentField from '../../../../FieldContainers/AddressFields/ApartmentField/ApartmentField';
import CityField from '../../../../FieldContainers/AddressFields/CityField/CityField';
import ZipCodeField from '../../../../FieldContainers/AddressFields/ZipCodeField/ZipCodeField';
import { Button, DynamicContainer } from '../../../../../importCL';
import AddressSuggestionPopup from '../../../../AddressSuggestionPopup/AddressSuggestionPopup';
import { generateFieldErrorMessage } from '../../../../../utils/error-message';
import AddressInvalidModal from '../../../../AddressInvalidModal/AddressInvalidModal';

type AddressType = 'Mailing' | 'Home';

interface IInputsPQDE {
    isInvalidAddressPopUpVariant?: boolean;
    cityStateRetrievalEnabled?: boolean;
    isSingleAddressField?: boolean;
    isAddressLockVariant?: boolean;
}

const dataMap: Record<string, keyof TPersonalInformation> = {
    fname: 'FirstName',
    mname: 'MiddleInitial',
    lname: 'LastName',
    address: 'MailingAddressLine1',
    apartment: 'MailingAddressLine2',
    city: 'MailingCity',
    state: 'MailingState',
    zipCode: 'MailingZipCode',
};

export default function Inputs(props: IInputsPQDE) {
    const regexContainer = regexValidation();
    const { validationRef } = useAddressValidationState();
    const addressRevision = useRef<number>(0);

    const handleBlur = async (addressType: AddressType, prefill?: boolean) => {
        const revision = addressRevision.current;
        await addressValidationSemaphore.acquire();
        if (props.isAddressLockVariant && revision !== addressRevision.current) {
            addressValidationSemaphore.release();
            return { isValid: false, data: null };
        }
        const result = await checkIfValid(addressType, prefill);
        addressValidationSemaphore.release();
        if (props.isAddressLockVariant && revision !== addressRevision.current) {
            return { isValid: false, data: null };
        }
        if (validationRef.current) {
            validationRef.current.isValid = result.isValid;
            validationRef.current.data = result.data;
        }
        if (props.isAddressLockVariant && result.isValid) {
            setLockedAddresses((current) => ({ ...current, [addressType]: true }));
        }
        return result;
    };

    const { personalInformation, setSingleValue, batchSetValues } = usePersonalInformation();
    const { setShowHomeAddress, showHomeAddress, batchSetValues: batchSetHomeValues, homeAddress } = useHomeAddress();
    const { homeAddressError, dispatch: homeDispatch } = useHomeAddressError();
    const { personalInformationError, dispatch } = usePersonalInformationError();

    const [control, setControl] = useState<any>();
    const [homeControl, setHomeControl] = useState<any>();
    const { initAutoComplete, setBasicListeners, getAddressData, pca, loqateEnabled, addressCacheRef, generateCacheKey, addToCache, searchAddressByZip } = useLoqate();
    const { matchGeneralDelivery } = useGeneralDelivery();
    const { aptUnitPoBox, ssnMessage, invalidAddressHeadline, suggestedAddressAcceptCtaText, suggestedAddressDeclineCtaText, suggestedAddressEnabled, suggestedAddressHeadline, suggestedAddressSubHeadline, avcThreshold, reEnterAddressCtaText: authoredReEnterAddressCtaText } = usePageProperties();

    const [prefill, setPrefill] = useState(false);
    const [lockedAddresses, setLockedAddresses] = useState<Record<AddressType, boolean>>({
        Mailing: false,
        Home: false,
    });
    const [showPopup, setShowPopup] = useState(false);
    const [loqateResponse, setLoqateResponse] = useState<any>({});
    const [mailingAddressPopupShown, setMailingAddressPopupShown] = useState(false);
    const [homeAddressPopupShown, setHomeAddressPopupShown] = useState(false);
    const acceptCtaFunctionRef = useRef(null);
    const declineCtaFunctionRef = useRef(null);
    const [hasAddressBeenFocused, setHasAddressBeenFocused] = useState(false);
    const [showInvalidAddressModal, setShowInvalidAddressModal] = useState(false);
    const [invalidAddressDisplay, setInvalidAddressDisplay] = useState('');
    const reEnterAddressCtaText = authoredReEnterAddressCtaText || 'Re-enter address';
    const invalidAddressRef = useRef<{
        address: string;
        attempts: number;
    }>({
        address: '',
        attempts: 0,
    });


    useEffect(() => {
        const postDataEl = document.querySelector<HTMLDivElement>('#skip-pre-post-data');
        if (postDataEl?.dataset && Object.keys(postDataEl?.dataset).length > 0) {
            const dataset: DOMStringMap = postDataEl.dataset;

            let nextValues: Partial<{ [x in keyof TPersonalInformation]: string }> = {};
            let mailingAddressFound = false;
            Object.entries(dataset).forEach(([key, value]) => nextValues[dataMap[key] as keyof TPersonalInformation] = value);

            if (Object.keys(nextValues).some((key) => [
                'MailingAddressLine1',
                'MailingCity',
                'MailingState',
                'MailingZipCode'
            ].includes(key))) {
                mailingAddressFound = true;
            }

            const mailingAddressStructureCheck = (!mailingAddressFound || 
                (
                    nextValues?.MailingAddressLine1 &&
                    nextValues?.MailingCity &&
                    nextValues?.MailingState &&
                    nextValues?.MailingZipCode
                )
            );

            const allowFunction = {
                FirstName: (value) =>
                    value.length <= 30 && regexContainer.allow.first_name.test(value),
                MiddleInitial: (value) =>
                    value.length <= 1 && regexContainer.allow.middle_initial.test(value),
                LastName: (value) =>
                    value.length <= 30 && regexContainer.allow.last_name.test(value),
                MailingState: (value) =>
                    mailingAddressStructureCheck &&
                    (stateMap.has(value.toLowerCase()) || [...stateMap.values()].includes(value.toUpperCase())),
                MailingAddressLine1: (value) =>
                    mailingAddressStructureCheck && value,
                MailingCity: (value) =>
                    mailingAddressStructureCheck && value,
                MailingZipCode: (value) =>
                    mailingAddressStructureCheck && value,
            };

            const transformFunction = {
                MailingState: (value) => value.toUpperCase(),
                MailingZipCode: (value) => value.substring(0, 5),
            }

            for (const entry in nextValues) {
                if (
                    nextValues[entry]
                    && (allowFunction[entry]
                        && !(allowFunction[entry](nextValues[entry])))
                ) {
                    if ([
                        'FirstName',
                        'MiddleInitial',
                        'LastName',
                    ].includes(entry)) {
                        nextValues = {
                            ...nextValues,
                            FirstName: '',
                            MiddleInitial: '',
                            LastName: '',
                        }

                    } else if ([
                        'MailingAddressLine1',
                        'MailingCity',
                        'MailingState',
                        'MailingZipCode',
                    ].includes(entry)) {
                        nextValues = {
                            ...nextValues,
                            MailingAddressLine1: '',
                            MailingAddressLine2: '',
                            MailingCity: '',
                            MailingState: '',
                            MailingZipCode: '',
                        }

                    } else {
                        nextValues[entry] = '';
                    }
                }

                if (
                    nextValues[entry] &&
                    transformFunction[entry]
                ) {
                    nextValues[entry] = transformFunction[entry](nextValues[entry]);
                }

            }
            batchSetValues(nextValues);
            setPrefill(true);
        } else {
            const appDeclineBroadcastChannel = new BroadcastChannel('PQ_BroadcastChannel');
            appDeclineBroadcastChannel.postMessage('Ready.');
            appDeclineBroadcastChannel.onmessage = (e) => {
                batchSetValues(e.data);
                if (e.data.HomeAddressLine1) {
                    batchSetHomeValues(e.data);
                    setShowHomeAddress(true);
                }

                setPrefill(true);
                appDeclineBroadcastChannel.close();
            };
        }
    }, []);

    const [validatedPrefill, setValidatedPrefill] = useState({
        Mailing: false,
        Home: false,
    });

    useEffect(() => {
        const checkPrefillValidity = async (type: AddressType) => {
            await handleBlur(type, true);
            setValidatedPrefill((prev) => ({ ...prev, [type]: true }));
        }
        if (prefill) {
            if (personalInformation.MailingAddressLine1 && !validatedPrefill.Mailing) {
                checkPrefillValidity('Mailing');
            }
            if (homeAddress.HomeAddressLine1 && !validatedPrefill.Home) {
                checkPrefillValidity('Home');
            }
        }
    }, [prefill, personalInformation, homeAddress]);

    useEffect(() => {
        if (pca && !control && loqateEnabled) {
            setControl(initAutoComplete('mailingAddressPQDE', 'aptUnitPQDE', 'cityPQDE', 'statePQDE', 'zipCodePQDE'));
        }
        if (pca && !homeControl && loqateEnabled) {
            setHomeControl(initAutoComplete('homeAddress-mailingAddressPQDE', 'homeAddress-aptUnitPQDE', 'homeAddress-cityPQDE', 'homeAddress-statePQDE', 'homeAddress-zipCodePQDE'));
        }
    }, [pca, showHomeAddress]);

    useEffect(() => {
        if (control) {
            setBasicListeners(control);
            control.listen('populate', (loqateAddress: any) => {
                const address = {
                    MailingAddressLine1: loqateAddress.FormattedLine1,
                    MailingAddressLine2: loqateAddress.SubBuilding,
                    MailingCity: loqateAddress.City,
                    MailingState: loqateAddress.Province,
                    MailingZipCode: loqateAddress.PostalCode,
                };

                setAddressErrors('Mailing', true);
                batchSetValues(address);
                const PoBoxFound = regexValidation().po_box_test.test(address.MailingAddressLine1) || (!!aptUnitPoBox && regexValidation().po_box_test.test(address.MailingAddressLine2));
                setShowHomeAddress(PoBoxFound);
                const cacheKey = generateCacheKey('Mailing', [
                    address.MailingAddressLine1,
                    address.MailingAddressLine2,
                    address.MailingCity,
                    address.MailingState,
                    address.MailingZipCode
                ]);

                //Populates should be valid addresses and residential
                addToCache(cacheKey, { address: cacheKey, isValid: true, isPOBoxOrCommercial: PoBoxFound, data: { valid: true, commerical: PoBoxFound } });

                if (props.isAddressLockVariant) {
                    addressRevision.current += 1;
                    setLockedAddresses((current) => ({ ...current, Mailing: true }));
                }

                setTimeout(() => {
                    document.getElementById('statePQDE')?.focus();
                    document.getElementById('statePQDE')?.blur();
                    document.getElementById('aptUnitPQDE')?.focus();
                    document.getElementById('aptUnitPQDE')?.blur();
                    document.getElementById('cityPQDE')?.focus();
                    document.getElementById('cityPQDE')?.blur();
                    document.getElementById('zipCodePQDE')?.focus();
                    document.getElementById('zipCodePQDE')?.blur();
                }, 0);

                validationRef.current.isValid = true;
                validationRef.current.data = loqateAddress;
            });
        }
    }, [control]);

    useEffect(() => {
        if (homeControl && showHomeAddress) {
            homeControl.load();
            setBasicListeners(homeControl);
            homeControl.listen('populate', (loqateAddress: any) => {
                const address = {
                    HomeAddressLine1: loqateAddress.FormattedLine1,
                    HomeAddressLine2: loqateAddress.SubBuilding,
                    HomeCity: loqateAddress.City,
                    HomeState: loqateAddress.Province,
                    HomeZipCode: loqateAddress.PostalCode,
                };
                const PoBoxFound = regexValidation().po_box_test.test(address.HomeAddressLine1) || (!!aptUnitPoBox && regexValidation().po_box_test.test(address.HomeAddressLine2));
                const cacheKey = generateCacheKey('Home', [
                    address.HomeAddressLine1,
                    address.HomeAddressLine2,
                    address.HomeCity,
                    address.HomeState,
                    address.HomeZipCode
                ]);
                //Populates should be valid addresses and residential
                addToCache(cacheKey, { address: cacheKey, isValid: true, isPOBoxOrCommercial: PoBoxFound, data: { valid: true, commerical: PoBoxFound } });

                setAddressErrors('Home', true);
                batchSetHomeValues(address);

                if (props.isAddressLockVariant && !PoBoxFound) {
                    addressRevision.current += 1;
                    if (validationRef.current) {
                        validationRef.current.isValid = true;
                        validationRef.current.data = loqateAddress;
                    }
                    setLockedAddresses((current) => ({ ...current, Home: true }));
                }

                setTimeout(() => {
                    document.getElementById('homeAddress-statePQDE')?.focus();
                    document.getElementById('homeAddress-statePQDE')?.blur();
                }, 0);
            });
        }
    }, [homeControl, showHomeAddress]);

    const trimSpace = (keyName: keyof TPersonalInformation, value: string) => {
        setSingleValue(keyName)(value.trim());
    };

    const checkRegex = (type: 'Home' | 'Mail' | 'Other', regexMatcher: keyof ReturnType<typeof regexValidation> | 'mailing_address', value: string, personalInformationKeyName?: keyof typeof personalInformation, homeAddressKeyName?: keyof typeof homeAddress) => {
        const newValue = value.trim();
        let regex: { [newValuekey: string]: any } = regexValidation();
        if (regexMatcher in regex.allow) {
            regex = regex.allow;
        }
        if (!regex[regexMatcher].test(newValue)) {
            if (type === 'Home' && homeAddressKeyName) {
                homeDispatch({
                    type: 'setValid',
                    target: homeAddressKeyName,
                    payload: false,
                });
                return false;
            } else if ((type === 'Mail' || type === 'Other') && personalInformationKeyName) {
                dispatch({
                    type: 'setValid',
                    target: personalInformationKeyName,
                    payload: false,
                });
                return false;
            }
        }

        return true;
    };

    const setAddressErrors = (addressType: AddressType, valid: boolean) => {
        const keys = [`${addressType}AddressLine1`, `${addressType}AddressLine2`, `${addressType}City`, `${addressType}State`, `${addressType}ZipCode`];
        const errors: Record<string, IErrorState> = {};

        keys.forEach((key) => {
            const errorState: IErrorState = {
                isInError: key.includes('AddressLine2') ? false : !valid,
                isValidValue: key.includes('AddressLine2') ? true : valid,
                required: !key.includes('AddressLine2'),
            };
            errors[key] = errorState;
        });

        if (addressType === 'Mailing') {
            dispatch({
                type: 'batchSetValid',
                payload: errors,
            });
        } else {
            homeDispatch({
                type: 'batchSetValid',
                payload: errors,
            });
        }

    };

    const handleValidFieldStates = (addressType: AddressType, data: { commercial: boolean, valid: boolean, suggestedAddress?: {}}, isPO?: boolean): void => {
        setAddressErrors(addressType, true);

        if (props.isAddressLockVariant) {
            setLockedAddresses((current) => ({ ...current, [addressType]: true }));
        }

        if (addressType === 'Mailing') {
            setShowHomeAddress(data.commercial || isPO);
        }
    }

    const handleReEnterAddress = (addressType: AddressType): void => {
        addressRevision.current += 1;
        if (addressType === 'Mailing') {
            batchSetValues({
                MailingAddressLine1: '',
                MailingAddressLine2: '',
                MailingCity: '',
                MailingState: '',
                MailingZipCode: '',
            });
            batchSetHomeValues({
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeZipCode: '',
            });
            setAddressErrors('Mailing', true);
            setAddressErrors('Home', true);
            setShowHomeAddress(false);
            setMailingAddressPopupShown(false);
            setHomeAddressPopupShown(false);
            setLockedAddresses({ Mailing: false, Home: false });
        } else {
            batchSetHomeValues({
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeZipCode: '',
            });
            setAddressErrors('Home', true);
            setHomeAddressPopupShown(false);
            setLockedAddresses((current) => ({ ...current, Home: false }));
        }

        if (validationRef.current) {
            validationRef.current.isValid = false;
            validationRef.current.data = null;
        }

        setTimeout(() => {
            document.getElementById(addressType === 'Mailing' ? 'mailingAddressPQDE' : 'homeAddress-mailingAddressPQDE')?.focus();
        }, 0);
    };

    const handleInvalidFieldStates = (addressType: AddressType) => {
        if (addressType === 'Mailing') {
            if (!prefill) {
                setAddressErrors(addressType, false);
            }
            const address = {
                MailingAddressLine1: '',
                MailingAddressLine2: '',
                MailingCity: '',
                MailingState: '',
                MailingZipCode: '',
            };
            batchSetValues(address);
            batchSetHomeValues({
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeZipCode: '',
            });
            setShowHomeAddress(false);
        } else {
            setAddressErrors(addressType, false);

            const address = {
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeZipCode: '',
            };
            batchSetHomeValues(address);
        }
    }

    const incrementInvalidAddressAttempts = (key: string): void => {
        if (invalidAddressRef.current.address === key) {
            invalidAddressRef.current.attempts += 1;
        } else {
            invalidAddressRef.current = {
                address: key,
                attempts: 1,
            };
        }
    };

    const buildAddressDisplay = (addressType: AddressType): string => {
        const addresses = {
            ...personalInformation,
            ...homeAddress,
        };

        return [
            addresses[`${addressType}AddressLine1`],
            addresses[`${addressType}AddressLine2`],
            addresses[`${addressType}City`],
            addresses[`${addressType}State`],
            addresses[`${addressType}ZipCode`],
        ]
            .filter(Boolean)
            .join(', ');
    };

    const checkIfValid = async (addressType: AddressType, prefill?: boolean): Promise<{ isValid: boolean, data: any }> => {
        const revision = addressRevision.current;
        try {
            const addresses = JSON.parse(JSON.stringify({ ...personalInformation, ...homeAddress }));
            const cache = addressCacheRef.current;
            const cacheKey = generateCacheKey(addressType, [
                addresses[`${addressType}AddressLine1`],
                addresses[`${addressType}AddressLine2`],
                addresses[`${addressType}City`],
                addresses[`${addressType}State`],
                addresses[`${addressType}ZipCode`]
            ]);

            if( addresses[`${addressType}AddressLine1`].length > 0 && addresses[`${addressType}AddressLine2`].length > 0 && 
                addresses[`${addressType}AddressLine1`].toLowerCase() === addresses[`${addressType}AddressLine2`].toLowerCase()
            ){
                const mailingAddress = {
                    MailingAddressLine1: '',
                    MailingAddressLine2: '',
                    MailingCity: '',
                    MailingState: '',
                    MailingZipCode: '',
                };
                const homeAddress = {
                    HomeAddressLine1: '',
                    HomeAddressLine2: '',
                    HomeCity: '',
                    HomeState: '',
                    HomeZipCode: '',
                };

                if (addressType === 'Mailing') {
                    batchSetHomeValues(homeAddress);
                    batchSetValues(mailingAddress);
                    setShowHomeAddress(false);
                }

                if (addressType === 'Home') {
                    batchSetHomeValues(homeAddress);
                }

                setAddressErrors(addressType, false);
                const result = { isValid: false, data: null };
                return result;
            }

            if (cache[cacheKey] &&
                addresses[`${addressType}AddressLine1`] &&
                addresses[`${addressType}City`] &&
                addresses[`${addressType}State`] &&
                addresses[`${addressType}ZipCode`]) {
                if (addressType === 'Mailing') {
                    //check if our cache is value is invalid or not
                    if (!cache[cacheKey].isValid) {
                        incrementInvalidAddressAttempts(cacheKey);

                        if (props.isInvalidAddressPopUpVariant && invalidAddressRef.current.attempts >= 2) {
                            setInvalidAddressDisplay(buildAddressDisplay(addressType));
                            handleInvalidFieldStates(addressType);
                            setShowInvalidAddressModal(true);

                            return {
                                isValid: false,
                                data: cache[cacheKey].data,
                            };
                        }

                        if (!prefill) {
                            setAddressErrors(addressType, false);
                        }
                        const address = {
                            MailingAddressLine1: '',
                            MailingAddressLine2: '',
                            MailingCity: '',
                            MailingState: '',
                            MailingZipCode: '',
                        };
                        batchSetValues(address);
                        setShowHomeAddress(false);
                        batchSetHomeValues({
                            HomeAddressLine1: '',
                            HomeAddressLine2: '',
                            HomeCity: '',
                            HomeState: '',
                            HomeZipCode: '',
                        });
                    } else {
                        setAddressErrors(addressType, true);
                        setShowHomeAddress(cache[cacheKey].isPOBoxOrCommercial);
                    }
                } else if (addressType === 'Home' && (cache[cacheKey].isPOBoxOrCommercial || !cache[cacheKey].isValid)) {
                    incrementInvalidAddressAttempts(cacheKey);
                    if (props.isInvalidAddressPopUpVariant && invalidAddressRef.current.attempts >= 2) {
                        setInvalidAddressDisplay(buildAddressDisplay(addressType));
                        handleInvalidFieldStates(addressType);
                        setShowInvalidAddressModal(true);

                        return {
                                isValid: false,
                                data: cache[cacheKey].data,
                            };
                        }
                    setAddressErrors(addressType, false);
                    const address = {
                        HomeAddressLine1: '',
                        HomeAddressLine2: '',
                        HomeCity: '',
                        HomeState: '',
                        HomeZipCode: '',
                    };
                    batchSetHomeValues(address);
                    return { isValid: false, data: cache[cacheKey].data };
                }
                return {
                    isValid: cache[cacheKey].isValid,
                    data: cache[cacheKey].data,
                };
            }
            const isPO = aptUnitPoBox ?
                homeAddressCheckRegex.test(addresses[`${addressType}AddressLine1`]) ||
                homeAddressCheckRegex.test(addresses[`${addressType}AddressLine2`]) :
                homeAddressCheckRegex.test(addresses[`${addressType}AddressLine1`]);

            if (addressType === 'Home' && matchGeneralDelivery(addresses[`${addressType}AddressLine1`])) {
                homeDispatch({
                    type: 'setValid',
                    target: 'HomeAddressLine1',
                    payload: false,
                });

                const address = {
                    HomeAddressLine1: '',
                    HomeAddressLine2: '',
                    HomeCity: '',
                    HomeState: '',
                    HomeZipCode: '',
                };
                batchSetHomeValues(address);
                const result = { isValid: false, data: null };
                return result;
            }

            if (loqateEnabled &&
                addresses[`${addressType}AddressLine1`] &&
                addresses[`${addressType}City`] &&
                addresses[`${addressType}State`] &&
                addresses[`${addressType}ZipCode`] &&
                !document.querySelector('.pcaselected:hover')) {

                const data = await getAddressData(
                    addresses[`${addressType}AddressLine1`],
                    addresses[`${addressType}AddressLine2`],
                    addresses[`${addressType}City`],
                    addresses[`${addressType}State`],
                    addresses[`${addressType}ZipCode`],
                    undefined,
                    suggestedAddressEnabled,
                    avcThreshold
                );

                if (props.isAddressLockVariant && revision !== addressRevision.current) {
                    return { isValid: false, data: null };
                }

                //add to cache regardless of validity
                addToCache(cacheKey, { address: cacheKey, isValid: data.valid, isPOBoxOrCommercial: data.commercial, data: data });

                if (data?.valid && addressType === 'Mailing') {
                    invalidAddressRef.current = {
                        address: '',
                        attempts: 0,
                    };
                    handleValidFieldStates(addressType, data, isPO);
                    const result = { isValid: true, data };
                    return result;
                } else if (addressType === 'Mailing') {
                    incrementInvalidAddressAttempts(cacheKey);
                    if (props.isInvalidAddressPopUpVariant && invalidAddressRef.current.attempts >= 2) {
                        setInvalidAddressDisplay(buildAddressDisplay(addressType));
                        handleInvalidFieldStates(addressType);
                        setShowInvalidAddressModal(true);

                        return {
                            isValid: false,
                            data,
                        };
                    }
                    if (suggestedAddressEnabled && !mailingAddressPopupShown && data.suggestedAddress) {
                        acceptCtaFunctionRef.current = () => {
                            handleValidFieldStates(addressType, data, isPO);
                            batchSetValues({
                                MailingAddressLine1: data.suggestedAddress.addressLine1,
                                MailingAddressLine2: data.suggestedAddress.addressLine2 || '',
                                MailingCity: data.suggestedAddress.city,
                                MailingState: data.suggestedAddress.state,
                                MailingZipCode: data.suggestedAddress.zipCode,
                            });
                            if (validationRef.current) {
                                validationRef.current.isValid = true;
                                validationRef.current.data = result.data;
                            }
                            setShowPopup(false);
                        }
                        declineCtaFunctionRef.current = () => {
                            handleInvalidFieldStates(addressType);
                            setShowPopup(false);
                            const mailingAddressInput = document.getElementById('mailingAddressPQDE');
                            mailingAddressInput.focus();
                        }
                        setLoqateResponse(data);
                        setShowPopup(true);
                        setMailingAddressPopupShown(true);
                    } else {
                        handleInvalidFieldStates(addressType);
                        const result = { isValid: false, data };
                        return result;
                    }   
                }

                if (data?.valid && !data?.commercial && !matchGeneralDelivery(addresses[`${addressType}AddressLine1`]) && addressType === 'Home') {
                    invalidAddressRef.current = {
                        address: '',
                        attempts: 0,
                    };
                    handleValidFieldStates(addressType, data);
                    const result = { isValid: true, data };
                    return result;
                } else if (addressType === 'Home') {
                    incrementInvalidAddressAttempts(cacheKey);
                    if (props.isInvalidAddressPopUpVariant && invalidAddressRef.current.attempts >= 2) {
                        setInvalidAddressDisplay(buildAddressDisplay(addressType));
                        handleInvalidFieldStates(addressType);
                        setShowInvalidAddressModal(true);

                        return {
                            isValid: false,
                            data,
                        };
                    }
                    if (suggestedAddressEnabled && !homeAddressPopupShown && data.suggestedAddress) {
                        acceptCtaFunctionRef.current = () => {
                            handleValidFieldStates(addressType, data);
                            batchSetHomeValues({
                                HomeAddressLine1: data.suggestedAddress.addressLine1,
                                HomeAddressLine2: data.suggestedAddress.addressLine2 || '',
                                HomeCity: data.suggestedAddress.city,
                                HomeState: data.suggestedAddress.state,
                                HomeZipCode: data.suggestedAddress.zipCode,
                            });                
                            if (validationRef.current) {
                                validationRef.current.isValid = true;
                                validationRef.current.data = result.data;
                            }            
                            setShowPopup(false);
                        }
                        declineCtaFunctionRef.current = () => {
                            handleInvalidFieldStates(addressType);
                            setShowPopup(false);
                            const mailingAddressInput = document.getElementById('homeAddress-mailingAddressPQDE');
                            mailingAddressInput.focus();
                        }
                        setLoqateResponse(data);
                        setShowPopup(true);
                        setHomeAddressPopupShown(true);
                    } else {
                        handleInvalidFieldStates(addressType);
                    }
                    const result = { isValid: false, data };
                    return result;
                }
            }
            const result = { isValid: false, data: null };
            return result;
        } catch (error) {
            const result = { isValid: false, data: null };
            return result;
        }
    };

    const checkAptUnitPoBox = (aptUnit: string, type: string) => {
        if (aptUnitPoBox && aptUnit) {
            const PoBoxFound = regexValidation().po_box_test.test(aptUnit);
            if (type === 'Mailing') {
                setShowHomeAddress(PoBoxFound);
            } else if (PoBoxFound && type === 'Home') {
                homeDispatch({
                    type: 'setValid',
                    target: 'HomeAddressLine1',
                    payload: false,
                });

                const address = {
                    HomeAddressLine1: '',
                    HomeAddressLine2: '',
                    HomeCity: '',
                    HomeState: '',
                    HomeZipCode: '',
                };
                batchSetHomeValues(address);
            }
        }
    };

    return (
        <>
            <DynamicContainer displayTag={true} tag='div' className='Row'>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <div id='firstName-field-container'>
                        <NameField
                            {...namesAndIds.FirstName}
                            name='First Name'
                            value={personalInformation.FirstName}
                            setValueCallback={(firstNameValue: string) => {
                                setSingleValue('FirstName')(firstNameValue);
                                dispatch({ type: 'setValid', target: 'FirstName', payload: true });
                            }}
                            onBlurCallback={(_, isValid) => {
                                dispatch({ type: 'setValid', target: 'FirstName', payload: isValid });
                            }}
                            error={personalInformationError.FirstName.isInError}
                            useSuccessIcon
                        />
                    </div>
                </DynamicContainer>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <MiddleInitialField
                        {...namesAndIds.MiddleInitial}
                        value={personalInformation.MiddleInitial}
                        setValueCallback={setSingleValue('MiddleInitial')}
                        useSuccessIcon
                    />
                </DynamicContainer>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <div id='lastName-field-container'>
                        <NameField
                            {...namesAndIds.LastName}
                            name='Last Name'
                            value={personalInformation.LastName}
                            setValueCallback={(lastNameValue: string) => {
                                setSingleValue('LastName')(lastNameValue);
                                dispatch({ type: 'setValid', target: 'LastName', payload: true });
                            }}
                            onBlurCallback={(_, isValid) => {
                                dispatch({ type: 'setValid', target: 'LastName', payload: isValid });
                            }}
                            error={personalInformationError.LastName.isInError}
                            useSuccessIcon
                        />
                    </div>
                </DynamicContainer>
            </DynamicContainer>
            <DynamicContainer displayTag={true} tag='div' className='Row'>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4' onFocus={() => setHasAddressBeenFocused(true)}>
                    <AutoFillFocusProvider>
                        <MailingAddressInput
                            checkRegex={checkRegex}
                            addressValidationCheck={() => {
                                handleBlur('Mailing');
                            }}
                            disabled={lockedAddresses.Mailing}
                             />
                    </AutoFillFocusProvider>
                </DynamicContainer>

                <DynamicContainer displayTag={true} tag='div' className={`Col COL-MD-4 ${(props.isSingleAddressField && !hasAddressBeenFocused) ? 'hidden' : ''}`}>
                    <ApartmentField alternate
                        {...namesAndIds.MailingAddressLine2}
                        maxLength={100}
                        value={personalInformation.MailingAddressLine2}
                        setValueCallback={(value: string) => {
                            const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                            if (containsInvalidCharacters) {
                                value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                            }

                            setSingleValue('MailingAddressLine2')(value);
                        }}
                        error={personalInformationError.MailingAddressLine2.isInError}
                        valid={[personalInformationError.MailingAddressLine2.isValidValue, (isValid: boolean) => {
                            dispatch({ type: 'setValid', target: 'MailingAddressLine2', payload: isValid });
                        }]}
                        onBlurCallback={() => {
                            handleBlur('Mailing');
                            checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                        }}
                        onKeyDownCallback={(e) => {
                            if (e.key === 'Enter') {
                                handleBlur('Mailing');
                                checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                            }
                        }}
                        disabled={lockedAddresses.Mailing}
                        useSuccessIcon
                        showSuccessIcon={lockedAddresses.Mailing ? personalInformation.MailingAddressLine2 !== '' : undefined}
                        addressType='mailing'
                        isPrefill={prefill}
                    />
                </DynamicContainer>
                {
                    // DPM-13736 -- A/B Test
                    props.cityStateRetrievalEnabled && (
                        <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                            <div id='mailingZipCode-field-container'>
                                <ZipCodeField
                                    required alternate
                                    errorText={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                    invalidErrorMessage={generateFieldErrorMessage('ZIP Code')}
                                    requiredMessage={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                    {...namesAndIds.MailingZipCode}
                                    value={personalInformation.MailingZipCode}
                                    setValueCallback={(val: string) => {
                                        setSingleValue('MailingZipCode')(val);
                                        dispatch({ type: 'setValid', target: 'MailingZipCode', payload: true });
                                    }}
                                    error={personalInformationError.MailingZipCode.isInError}
                                    valid={[personalInformationError.MailingZipCode.isValidValue, (isValid: boolean) => {
                                        dispatch({ type: 'setValid', target: 'MailingZipCode', payload: isValid });
                                    }]}
                                    onBlurCallback={async () => {
                                        handleBlur('Mailing');
                                        checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');

                                        if (!personalInformation.MailingCity || !personalInformation.MailingState) {
                                            const { AdministrativeArea, Locality } = await searchAddressByZip(personalInformation.MailingZipCode);

                                            if (!personalInformation.MailingState) {
                                                setSingleValue('MailingState')(AdministrativeArea.toUpperCase());
                                                document.getElementById('statePQDE')?.focus();
                                                document.getElementById('statePQDE')?.blur();
                                            }
                                            if (!personalInformation.MailingCity) {
                                                setSingleValue('MailingCity')(Locality);
                                                document.getElementById('cityPQDE')?.focus();
                                                document.getElementById('cityPQDE')?.blur();
                                            }
                                        }
                                    }}
                                    onKeyDownCallback={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter') {
                                            handleBlur('Mailing');
                                            checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                        }
                                    }}
                                    disabled={lockedAddresses.Mailing}
                                    useOutsideValidation={personalInformationError.MailingZipCode.isValidValue}
                                    isPrefill={prefill}
                                    useSuccessIcon
                                    showSuccessIcon={lockedAddresses.Mailing || undefined}
                                />
                            </div>
                        </DynamicContainer>
                    )
                }
                {
                    !props.cityStateRetrievalEnabled && 
                    <DynamicContainer displayTag={true} tag='div' className={`Col COL-MD-4 ${(props.isSingleAddressField && !hasAddressBeenFocused) ? 'hidden' : ''}`}>
                        <div id='mailingCity-field-container'>
                            <CityField
                                errorText={generateFieldErrorMessage('City', 'missingValue')}
                                requiredMessage={generateFieldErrorMessage('City', 'missingValue')}
                                alternate
                                required
                                {...namesAndIds.MailingCity}
                                value={personalInformation.MailingCity}
                                setValueCallback={(val: string) => {
                                    setSingleValue('MailingCity')(val);
                                    dispatch({ type: 'setValid', target: 'MailingCity', payload: true });
                                }}
                                error={personalInformationError.MailingCity.isInError}
                                valid={[personalInformationError.MailingCity.isValidValue, (isValid: boolean) => {
                                    dispatch({ type: 'setValid', target: 'MailingCity', payload: isValid });
                                }]}
                                onBlurCallback={() => {
                                    trimSpace('MailingCity', personalInformation.MailingCity);
                                    handleBlur('Mailing');
                                    checkRegex('Mail', 'city', personalInformation.MailingCity, 'MailingCity');
                                    checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                }}
                                onKeyDownCallback={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        trimSpace('MailingCity', personalInformation.MailingCity);
                                        handleBlur('Mailing');
                                        checkRegex('Mail', 'city', personalInformation.MailingCity, 'MailingCity');
                                        checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                    }
                                }}
                                disabled={lockedAddresses.Mailing}
                                useSuccessIcon
                                showSuccessIcon={lockedAddresses.Mailing || undefined}
                                useOutsideValidation={personalInformationError.MailingCity.isValidValue}
                                isPrefill={prefill}
                            />
                        </div>
                    </DynamicContainer>
                }
            </DynamicContainer>
            <DynamicContainer displayTag={true} tag='div' className={`Row justify-start ${(props.isSingleAddressField && !hasAddressBeenFocused) ? 'hidden' : ''}`}>
            {
                    // DPM-13736 -- A/B Test
                    props.cityStateRetrievalEnabled && (
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                        <div id='mailingCity-field-container'>
                            <CityField
                                errorText={generateFieldErrorMessage('City', 'missingValue')}
                                requiredMessage={generateFieldErrorMessage('City', 'missingValue')}
                                alternate
                                required
                                {...namesAndIds.MailingCity}
                                value={personalInformation.MailingCity}
                                setValueCallback={(val: string) => {
                                    setSingleValue('MailingCity')(val);
                                    dispatch({ type: 'setValid', target: 'MailingCity', payload: true });
                                }}
                                error={personalInformationError.MailingCity.isInError}
                                valid={[personalInformationError.MailingCity.isValidValue, (isValid: boolean) => {
                                    dispatch({ type: 'setValid', target: 'MailingCity', payload: isValid });
                                }]}
                                onBlurCallback={() => {
                                    trimSpace('MailingCity', personalInformation.MailingCity);
                                    handleBlur('Mailing');
                                    checkRegex('Mail', 'city', personalInformation.MailingCity, 'MailingCity');
                                    checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                }}
                                onKeyDownCallback={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        trimSpace('MailingCity', personalInformation.MailingCity);
                                        handleBlur('Mailing');
                                        checkRegex('Mail', 'city', personalInformation.MailingCity, 'MailingCity');
                                        checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                    }
                                }}
                                disabled={lockedAddresses.Mailing}
                                useSuccessIcon
                                showSuccessIcon={lockedAddresses.Mailing || undefined}
                                useOutsideValidation={personalInformationError.MailingCity.isValidValue}
                                isPrefill={prefill}
                            />
                        </div>
                    </DynamicContainer>
                    )
                }
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <div id='mailingState-field-container'>
                        <StateDropdownInput
                            key={lockedAddresses.Mailing ? 'mailing-locked' : 'mailing-editable'}
                            addressValidationCheck={() => {
                                handleBlur('Mailing');
                                checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleBlur('Mailing');
                                    checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                }
                            }}
                            isPrefill={prefill}
                            disabled={lockedAddresses.Mailing}
                             />
                    </div>
                </DynamicContainer>
                {
                    // DPM-13736 -- A/B Test
                    !props.cityStateRetrievalEnabled && (
                        <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                            <div id='mailingZipCode-field-container'>
                                <ZipCodeField
                                    required alternate
                                    errorText={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                    invalidErrorMessage={generateFieldErrorMessage('ZIP Code')}
                                    requiredMessage={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                    {...namesAndIds.MailingZipCode}
                                    value={personalInformation.MailingZipCode}
                                    setValueCallback={(val: string) => {
                                        setSingleValue('MailingZipCode')(val);
                                        dispatch({ type: 'setValid', target: 'MailingZipCode', payload: true });
                                    }}
                                    error={personalInformationError.MailingZipCode.isInError}
                                    valid={[personalInformationError.MailingZipCode.isValidValue, (isValid: boolean) => {
                                        dispatch({ type: 'setValid', target: 'MailingZipCode', payload: isValid });
                                    }]}
                                    onBlurCallback={() => {
                                        if (personalInformation.MailingZipCode.length === 5) {
                                            handleBlur('Mailing');
                                        };
                                        checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                    }}
                                    onKeyDownCallback={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter') {
                                            handleBlur('Mailing');
                                            checkAptUnitPoBox(personalInformation.MailingAddressLine2, 'Mailing');
                                        }
                                    }}
                                    disabled={lockedAddresses.Mailing}
                                    useOutsideValidation={personalInformationError.MailingZipCode.isValidValue}
                                    isPrefill={prefill}
                                    useSuccessIcon
                                    showSuccessIcon={lockedAddresses.Mailing || undefined}
                                />
                            </div>
                        </DynamicContainer>
                    )
                }
            </DynamicContainer>
            {lockedAddresses.Mailing &&
                <DynamicContainer displayTag={true} tag='div' className='Row'>
                    <DynamicContainer displayTag={true} tag='div' className='Col'>
                        <Button
                            variant='link'
                            size='small'
                            className='!px-0'
                            aria-label={`${reEnterAddressCtaText} for Mailing Address`}
                            onClick={() => handleReEnterAddress('Mailing')}
                        >
                            {reEnterAddressCtaText}
                        </Button>
                    </DynamicContainer>
                </DynamicContainer>
            }
            <HomeAddressFields
                addressValidationCheck={() => {
                    handleBlur('Home');
                }}
                checkRegex={checkRegex}
                checkAptUnitPoBox={checkAptUnitPoBox}
                prefill={prefill} 
                isLocked={lockedAddresses.Home}
                onReEnter={() => handleReEnterAddress('Home')}
                reEnterAddressCtaText={reEnterAddressCtaText}
            />
            <DynamicContainer displayTag={true} tag='div' className='Row mb-4'>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <AutoFillFocusProvider>
                        <EmailInput/>
                    </AutoFillFocusProvider>
                </DynamicContainer>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <div id='socialSecurity-field-container'>
                        <SsnField
                            {...namesAndIds.Ssn}
                            value={personalInformation.Ssn}
                            setValueCallback={(val: string) => {
                                setSingleValue('Ssn')(val);
                            }}
                            error={personalInformationError.Ssn.isInError}
                            valid={[personalInformationError.Ssn.isValidValue, (isValid: boolean) => {
                                dispatch({ type: 'setValid', target: 'Ssn', payload: isValid });
                            }]}
                            disclaimerMessage={ssnMessage}
                            required
                        />
                    </div>
                </DynamicContainer>
                <DynamicContainer displayTag={true} tag='div' className='Col COL-MD-4'>
                    <div id='dob-field-container'>
                        <DateOfBirthField
                            {...namesAndIds.dob}
                            alternate
                            className='PqDataEntryForm__date'
                            inputClass='PersonalInformation__input--dob PersonalInformation__input'
                            ctaId='BasicSubmitForm'
                            value={personalInformation.dob}
                            setValueCallback={(val: string) => {
                                setSingleValue('dob')(val);
                            }}
                            required
                            requiredMessage={generateFieldErrorMessage('Date of Birth', personalInformation.dob.length > 0 ? null : 'missingValue')}
                            valid={[personalInformationError.dob.isValidValue, useCallback((isValid: boolean) => {
                                dispatch({ type: 'setValid', target: 'dob', payload: isValid });
                            }, [])]}
                            applicantStateOrAu={homeAddress.HomeState || personalInformation.MailingState}
                            errorText={generateFieldErrorMessage('Date of Birth', personalInformation.dob.length > 0 ? null : 'missingValue')}
                            validateWithEnter
                            useSuccessIcon
                        />
                    </div>
                </DynamicContainer>
                {showPopup &&
                    <AddressSuggestionPopup
                        suggestedAddress={loqateResponse.suggestedAddress}
                        headline={suggestedAddressHeadline}
                        subHeadline={suggestedAddressSubHeadline}
                        acceptCtaText={suggestedAddressAcceptCtaText}
                        declineCtaText={suggestedAddressDeclineCtaText}
                        acceptCtaCallback={acceptCtaFunctionRef.current}
                        declineCtaCallback={declineCtaFunctionRef.current}
                    />}
                {props.isInvalidAddressPopUpVariant && showInvalidAddressModal &&
                    <AddressInvalidModal
                        headline={invalidAddressHeadline}
                        invalidAddress={invalidAddressDisplay}
                        closeCallback={() => setShowInvalidAddressModal(false)}
                    />
                }
            </DynamicContainer>
        </>
    );
}
